import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebase';
import './NextYearInterestForm.css';

function NextYearInterestForm({ fromEditionYear, source = 'archive', variant = 'card', onSuccess }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [nextYearConfig, setNextYearConfig] = useState(null);
  const nextYear = Number(fromEditionYear) + 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, '_config', 'nextYearInterestConfig'));
        if (cancelled) return;
        setNextYearConfig(snap.exists() ? snap.data() : null);
      } catch {
        if (!cancelled) setNextYearConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const datesBlock = useMemo(() => {
    const headline = (nextYearConfig?.headline || '').toString().trim();
    const datesText = (nextYearConfig?.datesText || '').toString().trim();
    const locationText = (nextYearConfig?.locationText || '').toString().trim();
    const noteText = (nextYearConfig?.noteText || '').toString().trim();
    const hasAny = headline || datesText || locationText || noteText;
    if (!hasAny) return null;
    return (
      <div className="interest-list-nextyear">
        {headline && <div className="interest-list-nextyear-headline">{headline.replaceAll('{year}', String(nextYear))}</div>}
        {datesText && <div className="interest-list-nextyear-dates">{datesText.replaceAll('{year}', String(nextYear))}</div>}
        {locationText && <div className="interest-list-nextyear-location">{locationText}</div>}
        {noteText && <div className="interest-list-nextyear-note">{noteText}</div>}
      </div>
    );
  }, [nextYearConfig, nextYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSaving(true);
    setMessage('');
    try {
      await addDoc(collection(firestore, 'nextYearInterest'), {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        fromEditionYear: Number(fromEditionYear),
        source,
        createdAt: serverTimestamp(),
      });
      setName('');
      setEmail('');
      setMessage("Thanks! You're on the interest list.");
      if (typeof onSuccess === 'function') onSuccess();
    } catch (err) {
      setMessage(err?.message || 'Unable to submit right now. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inner = (
    <>
      <h3>Join the {nextYear} Interest List</h3>
      {datesBlock}
      <p>Want updates when registration opens? Leave your name and email.</p>
      <form className="interest-list-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button type="submit" disabled={saving}>
          {saving ? 'Submitting...' : 'Join interest list'}
        </button>
      </form>
      {message && <p className="interest-list-message">{message}</p>}
    </>
  );

  if (variant === 'bare') return <div className="interest-list-bare">{inner}</div>;
  return (
    <section className="interest-list-card" aria-label="Join next year interest list">
      {inner}
    </section>
  );
}

export default NextYearInterestForm;
